import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Plus, X, UserCheck, UserX, Trash2 } from 'lucide-react';
import api from '../../api/client';

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h3 className="font-heading text-lg font-bold text-[#561D1A]">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

const roleColors = {
  super_admin: 'bg-purple-100 text-purple-700',
  admin: 'bg-blue-100 text-blue-700',
  merchant: 'bg-orange-100 text-orange-700',
  shopper: 'bg-gray-100 text-gray-700',
};

export default function UsersTab() {
  const [users, setUsers] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '', role: 'merchant', store_id: '' });
  const [saving, setSaving] = useState(false);
  const [filterRole, setFilterRole] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    const params = filterRole ? `?role=${filterRole}` : '';
    const [uRes, sRes] = await Promise.all([
      api.get(`/api/auth/admin/users${params}`).catch(() => ({ data: [] })),
      api.get('/api/admin/stores').catch(() => ({ data: [] })),
    ]);
    setUsers(uRes.data);
    setStores(sRes.data.filter(s => s.is_active));
    setLoading(false);
  };

  useEffect(() => { load(); }, [filterRole]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/api/auth/admin/users', { ...form, store_id: form.store_id || null });
      toast.success('User created');
      setModalOpen(false);
      load();
    } catch (err) {
      toast.error(err.displayMessage || 'Failed to create');
    } finally { setSaving(false); }
  };

  const toggleActive = async (user) => {
    await api.put(`/api/auth/admin/users/${user.id}`, { is_active: !user.is_active });
    toast.success(`User ${user.is_active ? 'deactivated' : 'activated'}`);
    load();
  };

  const toggleSelect = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(prev => prev.size === users.length ? new Set() : new Set(users.map(u => u.id)));
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user permanently?')) return;
    try { await api.delete(`/api/auth/admin/users/${id}`); toast.success('User deleted'); setSelected(prev => { const n = new Set(prev); n.delete(id); return n; }); load(); }
    catch { toast.error('Delete failed'); }
  };
  const handleBulkDelete = async () => {
    if (!selected.size || !window.confirm(`Delete ${selected.size} user(s) permanently?`)) return;
    setDeleting(true);
    try { const res = await api.post('/api/auth/admin/users/bulk-delete', { ids: [...selected] }); toast.success(`${res.data.deleted} user(s) deleted`); setSelected(new Set()); load(); }
    catch { toast.error('Bulk delete failed'); }
    finally { setDeleting(false); }
  };

  return (
    <div data-testid="users-tab">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2 text-sm">
          <span className="text-gray-500 text-xs">Role:</span>
          <select value={filterRole} onChange={e => setFilterRole(e.target.value)} className="outline-none bg-transparent text-sm">
            <option value="">All</option>
            <option value="admin">Admin</option>
            <option value="merchant">Merchant</option>
            <option value="shopper">Shopper</option>
          </select>
        </div>
        <p className="text-sm text-gray-500 flex-1">{users.length} user(s)</p>
        <button onClick={() => setModalOpen(true)} className="flex items-center gap-2 bg-[#EC1A23] text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-[#C4121A]" data-testid="add-user-btn">
          <Plus size={15} /> Add User
        </button>
      </div>

      {/* Bulk Actions Bar */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 mb-3 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5" data-testid="bulk-action-bar">
          <span className="text-sm text-red-700 font-medium">{selected.size} selected</span>
          <button onClick={handleBulkDelete} disabled={deleting}
            className="flex items-center gap-1.5 text-xs bg-red-600 text-white px-3 py-1.5 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
            data-testid="bulk-delete-users-btn">
            <Trash2 size={12} /> {deleting ? 'Deleting...' : 'Delete Selected'}
          </button>
          <button onClick={() => setSelected(new Set())} className="text-xs text-gray-500 hover:text-gray-700 ml-auto">Clear</button>
        </div>
      )}

      {loading ? (
        <div className="space-y-2">{[...Array(4)].map((_, i) => <div key={i} className="h-14 bg-gray-100 rounded-xl animate-pulse" />)}</div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-x-auto">
          <table className="w-full text-sm min-w-[600px]">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-3 py-3 w-10">
                  <input type="checkbox" checked={selected.size > 0 && selected.size === users.length}
                    onChange={toggleAll} className="rounded border-gray-300 accent-[#EC1A23]" data-testid="select-all-users" />
                </th>
                {['Name', 'Email', 'Role', 'Store', 'Status', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs uppercase tracking-wider text-gray-500 font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {users.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-10 text-gray-400">No users found</td></tr>
              ) : users.map(u => (
                <tr key={u.id} className={`hover:bg-gray-50 ${selected.has(u.id) ? 'bg-red-50/30' : ''}`} data-testid="user-row">
                  <td className="px-3 py-3">
                    <input type="checkbox" checked={selected.has(u.id)} onChange={() => toggleSelect(u.id)}
                      className="rounded border-gray-300 accent-[#EC1A23]" data-testid="select-user" />
                  </td>
                  <td className="px-4 py-3 font-medium text-gray-800">{u.name}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${roleColors[u.role] || 'bg-gray-100 text-gray-600'}`}>
                      {u.role?.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400">
                    {stores.find(s => s.id === u.store_id)?.name || '—'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${u.is_active ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500'}`}>
                      {u.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => toggleActive(u)} className={`p-1.5 rounded-lg transition-colors ${u.is_active ? 'text-orange-500 hover:bg-orange-50' : 'text-emerald-500 hover:bg-emerald-50'}`} data-testid="toggle-user-btn">
                        {u.is_active ? <UserX size={14} /> : <UserCheck size={14} />}
                      </button>
                      <button onClick={() => handleDelete(u.id)} className="text-gray-300 hover:text-red-500 p-1.5 rounded-lg hover:bg-red-50 transition-colors" data-testid="delete-user-btn">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalOpen && (
        <Modal title="Add User" onClose={() => setModalOpen(false)}>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Full Name *</label>
              <input required value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))}
                className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="user-name-input" />
            </div>
            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Email *</label>
              <input required type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))}
                className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="user-email-input" />
            </div>
            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Phone</label>
              <input type="tel" value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))}
                className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" />
            </div>
            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Password *</label>
              <input required type="password" value={form.password} onChange={e => setForm(f => ({...f, password: e.target.value}))}
                className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="user-password-input" />
            </div>
            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Role *</label>
              <select value={form.role} onChange={e => setForm(f => ({...f, role: e.target.value}))}
                className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="user-role-select">
                <option value="merchant">Merchant</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            {form.role === 'merchant' && stores.length > 0 && (
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Assign Store</label>
                <select value={form.store_id} onChange={e => setForm(f => ({...f, store_id: e.target.value}))}
                  className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="user-store-select">
                  <option value="">No store</option>
                  {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            )}
            <button type="submit" disabled={saving} className="w-full bg-[#EC1A23] hover:bg-[#C4121A] disabled:opacity-60 text-white py-3 rounded-full text-sm font-medium" data-testid="save-user-btn">
              {saving ? 'Creating...' : 'Create User'}
            </button>
          </form>
        </Modal>
      )}
    </div>
  );
}
